import React from "react";
import PropTypes from "prop-types";
import { Languages } from "lucide-react";

import { useTranslation } from "../context/Internationalization";
import useGraphql from "../Hooks/useGraphql";
import useLog from "../Hooks/useLog";
import { getStorage } from "../auth/utils";
import { EDIT_USER } from "../graphql/userGraphql";
import {
  DropdownMenuItem,
  DropdownMenuShortcut,
} from "../shadcn/components/ui/dropdown-menu";
import { Switch } from "../shadcn/components/ui/switch";
import { toast } from "sonner";

// Switches between Greek and English and saves the choice for signed-in users.
export const useLanguageToggle = () => {
  const { switchLanguage, lang } = useTranslation();
  const { axiosGraphQL } = useGraphql();
  const { errorLog } = useLog();

  const updateUserLanguage = async (language) => {
    try {
      await axiosGraphQL({ query: EDIT_USER, variables: { language } });
    } catch (error) {
      errorLog(error, "TranslationToggle", "updateUserLanguage", "EDIT_USER");
      toast.error(error.message);
    }
  };

  const toggleLanguage = () => {
    const language = lang === "gr" ? "en" : "gr";
    switchLanguage(language);
    const isLoggedUser = getStorage().getItem("user") !== null;
    if (isLoggedUser) updateUserLanguage(language);
  };

  return { lang, toggleLanguage };
};

// `menuItem` renders an item for a shadcn DropdownMenu; otherwise a switch.
const TranslationToggle = ({ menuItem = false, onChanged }) => {
  const { lang, toggleLanguage } = useLanguageToggle();
  const { t } = useTranslation();

  const onChangeLanguage = () => {
    toggleLanguage();
    onChanged?.();
  };

  if (menuItem) {
    return (
      <DropdownMenuItem onClick={onChangeLanguage}>
        <Languages aria-hidden="true" />
        {t("change_language")}
        <DropdownMenuShortcut aria-hidden="true">
          {lang === "gr" ? "EL" : "EN"}
        </DropdownMenuShortcut>
      </DropdownMenuItem>
    );
  }

  return (
    <Switch
      checked={lang === "gr"}
      onCheckedChange={onChangeLanguage}
      aria-label={lang === "gr" ? t("switch_to_english") : t("switch_to_greek")}
    />
  );
};

TranslationToggle.propTypes = {
  menuItem: PropTypes.bool,
  onChanged: PropTypes.func,
};

export default TranslationToggle;
